import { useState } from "react";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";

export const BENEFIT_TYPES = ["Public recognition", "Resident amenity access", "Rent credit", "Repayment terms", "No benefit (gift of support)"];
const GROUPS = [["owner", "For the owner"], ["residents", "For residents"], ["contributors", "For contributors"]];
const strip = (list) => (list || []).map(({ text, type }) => ({ text, type: type || null }));

export const ContributorBenefitInput = ({ value, onChange, testid = "contributor-benefit" }) => (
  <div className="grid gap-2 sm:grid-cols-[minmax(0,14rem)_1fr]">
    <select value={value.type || ""} onChange={(e) => onChange({ ...value, type: e.target.value || null })} className="h-11 rounded-xl border border-[var(--line)] bg-white/70 px-2 text-sm outline-none focus:border-[var(--charcoal)]" data-testid={`${testid}-type-select`}>
      <option value="">Benefit type…</option>
      {BENEFIT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
    </select>
    <input value={value.text} onChange={(e) => onChange({ ...value, text: e.target.value })} placeholder="Describe it, e.g. Name on a thank-you plaque" className="h-11 rounded-xl border border-[var(--line)] bg-white/70 px-3 text-sm outline-none focus:border-[var(--charcoal)]" data-testid={`${testid}-text-input`} />
  </div>
);

export const BenefitsEditor = ({ p, onChange, onClose }) => {
  const [b, setB] = useState({ owner: strip(p.benefits.owner), residents: strip(p.benefits.residents), contributors: strip(p.benefits.contributors) });
  const [busy, setBusy] = useState(false);
  const set = (k, i, v) => setB({ ...b, [k]: b[k].map((x, j) => (j === i ? v : x)) });
  const save = async () => {
    setBusy(true);
    const body = Object.fromEntries(Object.entries(b).map(([k, v]) => [k, v.filter((x) => x.text.trim()).map((x) => ({ text: x.text.trim(), type: x.type }))]));
    try { const { data } = await api.put(`/proposals/${p.id}/benefits`, body); onChange(data); onClose(); toast(p.isClaimant ? "Benefits saved — Set by owner" : "Benefits saved"); } catch (e) { toast(errMsg(e, "Couldn't save benefits.")); }
    setBusy(false);
  };
  return (
    <div className="mt-4 grid gap-6 rounded-xl border border-[var(--line)] bg-white/50 p-5" data-testid="benefits-editor">
      {GROUPS.map(([k, label]) => (
        <div key={k} className="grid gap-2">
          <div className="text-sm font-medium">{label}</div>
          {b[k].map((x, i) => (
            <div key={i} className="flex items-start gap-2">
              <div className="flex-1">{k === "contributors"
                ? <ContributorBenefitInput value={x} onChange={(v) => set(k, i, v)} testid={`edit-${k}-${i}`} />
                : <input value={x.text} onChange={(e) => set(k, i, { ...x, text: e.target.value })} className="h-11 w-full rounded-xl border border-[var(--line)] bg-white/70 px-3 text-sm outline-none focus:border-[var(--charcoal)]" data-testid={`edit-${k}-${i}-text-input`} />}
              </div>
              <button className="flex h-11 w-11 items-center justify-center text-[var(--stone-dark)] hover:text-[var(--charcoal)]" onClick={() => setB({ ...b, [k]: b[k].filter((_, j) => j !== i) })} aria-label="Remove" data-testid={`edit-${k}-${i}-remove`}><X size={16} /></button>
            </div>
          ))}
          <button className="btn-quiet w-fit" onClick={() => setB({ ...b, [k]: [...b[k], { text: "", type: null }] })} data-testid={`edit-${k}-add`}><Plus size={14} /> Add</button>
        </div>
      ))}
      <div className="flex gap-2">
        <button className="btn-primary" disabled={busy} onClick={save} data-testid="benefits-save-button">{p.isClaimant ? "Save as owner" : "Save"}</button>
        <button className="btn-quiet" onClick={onClose} data-testid="benefits-cancel-button">Cancel</button>
      </div>
    </div>
  );
};
